import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Switch,
  FormControlLabel,
  TablePagination,
  Collapse,
} from '@mui/material';
import {
  Security,
  History,
  Message,
  PersonAdd,
  PersonRemove,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Login,
  Logout,
  VpnKey,
  Shield,
  Warning,
  Error,
  Info,
  CheckCircle,
  AccessTime,
  Computer,
  PhoneAndroid,
  Tablet,
  Search,
  FilterList,
  Download,
  Refresh,
  MoreVert,
  ExpandMore,
  ExpandLess,
  Timeline,
  Analytics,
  Assignment,
  PersonPin,
  DirectionsCar,
  Payment,
  Support,
  AdminPanelSettings,
  DataUsage,
  Storage,
  CloudSync,
  Backup,
  Archive,
  RestoreFromTrash,
  VerifiedUser,
  ReportProblem,
  Notifications,
  Email,
  Sms,
  Phone,
  ChatBubble,
  Send,
  Inbox,
  Outbox,
  DraftsOutlined,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { adminStaffService } from '../../services/adminStaffService';

interface AuditEvent {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userRole: string;
  action: string;
  category: 'authentication' | 'user-management' | 'data-modification' | 'system-access' | 'security' | 'financial' | 'compliance';
  resource: string;
  resourceId?: string;
  ipAddress: string;
  userAgent: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'api';
  location: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  outcome: 'success' | 'failure' | 'pending';
  details: {
    oldValue?: any;
    newValue?: any;
    reason?: string;
    additionalData?: Record<string, any>;
  };
  correlationId?: string;
  sessionId: string;
}

interface MessageLog {
  id: string;
  timestamp: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'driver' | 'admin' | 'system';
  recipientId: string;
  recipientName: string;
  recipientType: 'user' | 'driver' | 'admin' | 'system';
  messageType: 'text' | 'image' | 'document' | 'system' | 'automated';
  content: string;
  channel: 'in-app' | 'sms' | 'email' | 'push' | 'whatsapp';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: 'booking' | 'support' | 'verification' | 'dispute' | 'notification' | 'marketing';
  metadata: {
    bookingId?: string;
    disputeId?: string;
    verificationId?: string;
    attachments?: string[];
    automated?: boolean;
    templateId?: string;
  };
  ipAddress?: string;
  deviceInfo?: string;
}

interface SecurityAlert {
  id: string;
  timestamp: string;
  type: 'suspicious-login' | 'multiple-failures' | 'privilege-escalation' | 'data-breach' | 'unusual-activity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  userName?: string;
  description: string;
  indicators: string[];
  ipAddress: string;
  location: string;
  status: 'active' | 'investigating' | 'resolved' | 'false-positive';
  assignedTo?: string;
  resolvedAt?: string;
  resolution?: string;
  affectedResources: string[];
}

interface DataChange {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  tableName: string;
  recordId: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  fieldChanges: {
    field: string;
    oldValue: any;
    newValue: any;
    dataType: string;
  }[];
  reason?: string;
  approvedBy?: string;
  rollbackAvailable: boolean;
}

interface SystemAccessLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userRole: string;
  action: 'login' | 'logout' | 'access-denied' | 'password-change' | 'permission-granted' | 'permission-revoked';
  resource: string;
  ipAddress: string;
  userAgent: string;
  location: string;
  sessionDuration?: number;
  outcome: 'success' | 'failure';
  failureReason?: string;
  mfaUsed: boolean;
  deviceFingerprint: string;
}

/**
 * Get human-readable location from IP address
 * Handles localhost IPs and unknown addresses gracefully
 */
const getLocationFromIP = (ipAddress: string | undefined): string => {
  if (!ipAddress || ipAddress === 'Unknown') {
    return 'Unknown';
  }

  // Detect localhost IPs (IPv4 and IPv6)
  const isLocalhost =
    ipAddress === '::1' ||
    ipAddress === '127.0.0.1' ||
    ipAddress.startsWith('127.') ||
    ipAddress === 'localhost';

  if (isLocalhost) {
    return 'Local/Development';
  }

  // Detect private/internal IPs
  const isPrivateIP =
    ipAddress.startsWith('10.') ||
    ipAddress.startsWith('192.168.') ||
    ipAddress.startsWith('172.16.') ||
    ipAddress.startsWith('172.17.') ||
    ipAddress.startsWith('172.18.') ||
    ipAddress.startsWith('172.19.') ||
    ipAddress.startsWith('172.2') ||
    ipAddress.startsWith('172.30.') ||
    ipAddress.startsWith('172.31.');

  if (isPrivateIP) {
    return 'Private Network';
  }

  // For real public IPs, we could use a geolocation service
  // For now, return a placeholder until backend provides location
  // Nigerian IP ranges: 105.x, 102.x, 197.x, 41.x, etc.
  if (ipAddress.startsWith('105.') ||
      ipAddress.startsWith('102.') ||
      ipAddress.startsWith('197.') ||
      ipAddress.startsWith('41.')) {
    return 'Nigeria'; // Likely Nigerian IP
  }

  // For other IPs, return Unknown until we have geolocation service
  return 'Unknown Location';
};

const AuditTrailPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState('24h');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);

  // State for API data
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch audit logs from API
  useEffect(() => {
    const fetchAuditLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await adminStaffService.getAuditLogs({
          page: page + 1, // Backend uses 1-indexed pagination
          limit: rowsPerPage,
        });

        if (response.success && response.data) {
          // Transform backend data to frontend format
          const transformedEvents: AuditEvent[] = response.data.logs.map((log: any) => {
            // Determine category from action prefix or resource type
            let category: AuditEvent['category'] = 'system-access';
            if (log.action?.includes('auth:') || log.action?.includes('login') || log.action?.includes('logout')) {
              category = 'authentication';
            } else if (log.action?.includes('user:') || log.action?.includes('staff:')) {
              category = 'user-management';
            } else if (log.resourceType === 'admin_staff' || log.resourceType === 'role_permissions') {
              category = 'user-management';
            }

            // Determine severity based on action
            let severity: AuditEvent['severity'] = 'low';
            if (log.action?.includes('delete') || log.action?.includes('suspend')) {
              severity = 'high';
            } else if (log.action?.includes('update') || log.action?.includes('create')) {
              severity = 'medium';
            }

            // Determine device type from user agent
            let deviceType: AuditEvent['deviceType'] = 'desktop';
            const ua = log.userAgent?.toLowerCase() || '';
            if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
              deviceType = 'mobile';
            } else if (ua.includes('tablet') || ua.includes('ipad')) {
              deviceType = 'tablet';
            } else if (ua.includes('api') || ua.includes('bot')) {
              deviceType = 'api';
            }

            return {
              id: log.id,
              timestamp: log.createdAt,
              userId: log.adminUserId || 'system',
              userName: log.adminEmail || 'System',
              userRole: log.adminRole || 'Admin',
              action: log.action,
              category,
              resource: log.resourceType || 'System',
              resourceId: log.resourceId,
              ipAddress: log.ipAddress || 'Unknown',
              userAgent: log.userAgent || 'Unknown',
              deviceType,
              location: getLocationFromIP(log.ipAddress),
              severity,
              outcome: log.status === 'success' ? 'success' : 'failure',
              details: {
                oldValue: log.oldValue,
                newValue: log.newValue,
                reason: log.errorMessage || '',
                additionalData: log.metadata || {}
              },
              sessionId: log.id // Use log ID as session ID
            };
          });

          setAuditEvents(transformedEvents);
          setTotalCount(response.data.pagination.total || response.data.pagination.totalCount || 0);
        } else {
          setError(response.error || 'Failed to fetch audit logs');
          setAuditEvents([]);
        }
      } catch (err: any) {
        console.error('Error fetching audit logs:', err);
        setError(err.message || 'Failed to fetch audit logs');
        setAuditEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [page, rowsPerPage]);

  // Sample data - REMOVED - now using real API data
  const oldAuditEvents: AuditEvent[] = [
    {
      id: '1',
      timestamp: '2024-01-15T14:30:22Z',
      userId: 'admin_001',
      userName: 'John Admin',
      userRole: 'System Administrator',
      action: 'User Account Modified',
      category: 'user-management',
      resource: 'User Profile',
      resourceId: 'user_12345',
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      deviceType: 'desktop',
      location: 'Lagos, Nigeria',
      severity: 'medium',
      outcome: 'success',
      details: {
        oldValue: { status: 'pending', role: 'user' },
        newValue: { status: 'active', role: 'driver' },
        reason: 'User verification completed'
      },
      sessionId: 'sess_789xyz'
    },
    {
      id: '2',
      timestamp: '2024-01-15T14:25:15Z',
      userId: 'user_12345',
      userName: 'Alice Driver',
      userRole: 'Driver',
      action: 'Failed Login Attempt',
      category: 'authentication',
      resource: 'Authentication System',
      ipAddress: '41.203.234.156',
      userAgent: 'PolarRide-App/1.2.3 (Android 12)',
      deviceType: 'mobile',
      location: 'Abuja, Nigeria',
      severity: 'high',
      outcome: 'failure',
      details: {
        reason: 'Invalid password - 3rd consecutive attempt',
        additionalData: { attemptCount: 3, accountLocked: true }
      },
      sessionId: 'sess_456def'
    },
    {
      id: '3',
      timestamp: '2024-01-15T14:20:45Z',
      userId: 'system',
      userName: 'System Process',
      userRole: 'System',
      action: 'Automated Data Backup',
      category: 'system-access',
      resource: 'Database Backup',
      ipAddress: '10.0.0.1',
      userAgent: 'BackupService/2.1.0',
      deviceType: 'api',
      location: 'Lagos Data Center',
      severity: 'low',
      outcome: 'success',
      details: {
        additionalData: { 
          backupSize: '2.3GB', 
          duration: '45 minutes',
          tablesBackedUp: 23
        }
      },
      sessionId: 'sess_backup123'
    },
    {
      id: '4',
      timestamp: '2024-01-15T14:15:30Z',
      userId: 'finance_002',
      userName: 'Sarah Finance',
      userRole: 'Finance Manager',
      action: 'Payment Record Updated',
      category: 'financial',
      resource: 'Payment Transaction',
      resourceId: 'pay_67890',
      ipAddress: '192.168.1.108',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      deviceType: 'desktop',
      location: 'Lagos, Nigeria',
      severity: 'high',
      outcome: 'success',
      details: {
        oldValue: { amount: 15000, status: 'pending' },
        newValue: { amount: 15000, status: 'completed' },
        reason: 'Manual payment confirmation'
      },
      sessionId: 'sess_fin456'
    }
  ];

  const messageLogs: MessageLog[] = [
    {
      id: '1',
      timestamp: '2024-01-15T14:35:20Z',
      conversationId: 'conv_001',
      senderId: 'user_123',
      senderName: 'Mike User',
      senderType: 'user',
      recipientId: 'driver_456',
      recipientName: 'James Driver',
      recipientType: 'driver',
      messageType: 'text',
      content: 'Hi, I\'m waiting at the pickup location. Where are you?',
      channel: 'in-app',
      status: 'read',
      priority: 'normal',
      category: 'booking',
      metadata: {
        bookingId: 'book_789',
        automated: false
      },
      ipAddress: '192.168.1.201',
      deviceInfo: 'iPhone 13 Pro'
    },
    {
      id: '2',
      timestamp: '2024-01-15T14:33:15Z',
      conversationId: 'conv_002',
      senderId: 'system',
      senderName: 'PolarRide System',
      senderType: 'system',
      recipientId: 'user_789',
      recipientName: 'Jane User',
      recipientType: 'user',
      messageType: 'system',
      content: 'Your booking has been confirmed. Driver will arrive in 5 minutes.',
      channel: 'push',
      status: 'delivered',
      priority: 'high',
      category: 'booking',
      metadata: {
        bookingId: 'book_321',
        automated: true,
        templateId: 'booking_confirmed'
      }
    },
    {
      id: '3',
      timestamp: '2024-01-15T14:30:45Z',
      conversationId: 'conv_003',
      senderId: 'support_001',
      senderName: 'Lisa Support',
      senderType: 'admin',
      recipientId: 'user_555',
      recipientName: 'David User',
      recipientType: 'user',
      messageType: 'text',
      content: 'Thank you for contacting support. We\'ve processed your refund request.',
      channel: 'email',
      status: 'sent',
      priority: 'normal',
      category: 'support',
      metadata: {
        disputeId: 'disp_123',
        automated: false
      }
    }
  ];

  const securityAlerts: SecurityAlert[] = [
    {
      id: '1',
      timestamp: '2024-01-15T14:25:00Z',
      type: 'multiple-failures',
      severity: 'high',
      userId: 'user_12345',
      userName: 'Alice Driver',
      description: 'Multiple failed login attempts detected',
      indicators: [
        '5 failed attempts in 10 minutes',
        'Different IP addresses used',
        'Account temporarily locked'
      ],
      ipAddress: '41.203.234.156',
      location: 'Abuja, Nigeria',
      status: 'investigating',
      assignedTo: 'security_team',
      affectedResources: ['user_account', 'authentication_system']
    },
    {
      id: '2',
      timestamp: '2024-01-15T13:45:30Z',
      type: 'suspicious-login',
      severity: 'medium',
      userId: 'admin_002',
      userName: 'Bob Admin',
      description: 'Login from unusual location detected',
      indicators: [
        'Login from Port Harcourt (usual: Lagos)',
        'New device fingerprint',
        'Outside normal working hours'
      ],
      ipAddress: '197.149.153.27',
      location: 'Port Harcourt, Nigeria',
      status: 'resolved',
      assignedTo: 'security_team',
      resolvedAt: '2024-01-15T14:10:00Z',
      resolution: 'Verified as legitimate - user traveling for business',
      affectedResources: ['admin_account']
    }
  ];

  const dataChanges: DataChange[] = [
    {
      id: '1',
      timestamp: '2024-01-15T14:30:22Z',
      userId: 'admin_001',
      userName: 'John Admin',
      tableName: 'users',
      recordId: 'user_12345',
      operation: 'UPDATE',
      fieldChanges: [
        {
          field: 'status',
          oldValue: 'pending',
          newValue: 'active',
          dataType: 'string'
        },
        {
          field: 'role',
          oldValue: 'user',
          newValue: 'driver',
          dataType: 'string'
        },
        {
          field: 'verified_at',
          oldValue: null,
          newValue: '2024-01-15T14:30:22Z',
          dataType: 'timestamp'
        }
      ],
      reason: 'User verification completed successfully',
      approvedBy: 'supervisor_001',
      rollbackAvailable: true
    },
    {
      id: '2',
      timestamp: '2024-01-15T14:15:30Z',
      userId: 'finance_002',
      userName: 'Sarah Finance',
      tableName: 'payments',
      recordId: 'pay_67890',
      operation: 'UPDATE',
      fieldChanges: [
        {
          field: 'status',
          oldValue: 'pending',
          newValue: 'completed',
          dataType: 'string'
        },
        {
          field: 'confirmed_at',
          oldValue: null,
          newValue: '2024-01-15T14:15:30Z',
          dataType: 'timestamp'
        },
        {
          field: 'confirmed_by',
          oldValue: null,
          newValue: 'finance_002',
          dataType: 'string'
        }
      ],
      reason: 'Manual payment verification completed',
      rollbackAvailable: false
    }
  ];

  const systemAccessLogs: SystemAccessLog[] = [
    {
      id: '1',
      timestamp: '2024-01-15T08:30:15Z',
      userId: 'admin_001',
      userName: 'John Admin',
      userRole: 'System Administrator',
      action: 'login',
      resource: 'Admin Dashboard',
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      location: 'Lagos, Nigeria',
      sessionDuration: 21600, // 6 hours in seconds
      outcome: 'success',
      mfaUsed: true,
      deviceFingerprint: 'fp_abc123def456'
    },
    {
      id: '2',
      timestamp: '2024-01-15T14:25:15Z',
      userId: 'user_12345',
      userName: 'Alice Driver',
      userRole: 'Driver',
      action: 'login',
      resource: 'Mobile App',
      ipAddress: '41.203.234.156',
      userAgent: 'PolarRide-App/1.2.3 (Android 12)',
      location: 'Abuja, Nigeria',
      outcome: 'failure',
      failureReason: 'Invalid password',
      mfaUsed: false,
      deviceFingerprint: 'fp_mobile789'
    }
  ];

  const activityStats = [
    { hour: '00', events: 45, messages: 12, alerts: 1 },
    { hour: '04', events: 23, messages: 8, alerts: 0 },
    { hour: '08', events: 156, messages: 89, alerts: 3 },
    { hour: '12', events: 234, messages: 145, alerts: 2 },
    { hour: '16', events: 189, messages: 112, alerts: 1 },
    { hour: '20', events: 98, messages: 67, alerts: 0 },
  ];

  const categoryDistribution = [
    { name: 'Authentication', value: 35, color: '#2196F3' },
    { name: 'User Management', value: 25, color: '#4CAF50' },
    { name: 'Data Modification', value: 20, color: '#FF9800' },
    { name: 'System Access', value: 12, color: '#9C27B0' },
    { name: 'Security', value: 5, color: '#F44336' },
    { name: 'Financial', value: 3, color: '#607D8B' },
  ];

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#F44336';
      case 'high': return '#FF5722';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const getOutcomeColor = (outcome: string): string => {
    switch (outcome) {
      case 'success': return '#4CAF50';
      case 'failure': return '#F44336';
      case 'pending': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'sent': return '#2196F3';
      case 'delivered': return '#4CAF50';
      case 'read': return '#9C27B0';
      case 'failed': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'authentication': return <VpnKey />;
      case 'user-management': return <PersonPin />;
      case 'data-modification': return <Edit />;
      case 'system-access': return <Computer />;
      case 'security': return <Shield />;
      case 'financial': return <Payment />;
      case 'compliance': return <VerifiedUser />;
      default: return <Assignment />;
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile': return <PhoneAndroid />;
      case 'tablet': return <Tablet />;
      case 'desktop': return <Computer />;
      case 'api': return <DataUsage />;
      default: return <Computer />;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Email />;
      case 'sms': return <Sms />;
      case 'push': return <Notifications />;
      case 'in-app': return <ChatBubble />;
      case 'whatsapp': return <Phone />;
      default: return <Message />;
    }
  };

  const formatDateTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const filteredAuditEvents = auditEvents.filter(event => {
    const matchesSearch = event.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.resource.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || event.severity === severityFilter;
    const matchesCategory = categoryFilter === 'all' || event.category === categoryFilter;
    const matchesUser = userFilter === '' || event.userName.toLowerCase().includes(userFilter.toLowerCase());
    return matchesSearch && matchesSeverity && matchesCategory && matchesUser;
  });

  const handleEventClick = (event: AuditEvent) => {
    setSelectedEvent(event);
    setDetailsDialogOpen(true);
  };

  const handleExport = (format: string) => {
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Audit Trail & Messaging Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive system activity tracking and message logging
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={realTimeEnabled} onChange={(e) => setRealTimeEnabled(e.target.checked)} />}
            label="Real-time"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<Archive />} onClick={() => setRetentionDialogOpen(true)}>
            Retention
          </Button>
          <Button variant="contained" startIcon={<Download />}>
            Export
          </Button>
        </Box>
      </Box>

      {/* Security Alerts Banner */}
      {securityAlerts.filter(alert => alert.status === 'active').length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning />
              Security Alerts Require Attention
            </Box>
          </AlertTitle>
          {securityAlerts.filter(alert => alert.status === 'active').length} active security alerts detected.
          <Button size="small" variant="outlined" sx={{ ml: 2 }}>
            View Security Dashboard
          </Button>
        </Alert>
      )}

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <History sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6">Total Events</Typography>
              </Box>
              <Typography variant="h4" component="div">
                1,247
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 24 hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Message sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6">Messages</Typography>
              </Box>
              <Typography variant="h4" component="div">
                8,934
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 24 hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning sx={{ color: 'warning.main', mr: 1 }} />
                <Typography variant="h6">Security Alerts</Typography>
              </Box>
              <Typography variant="h4" component="div">
                7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                2 active, 5 resolved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Storage sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="h6">Data Size</Typography>
              </Box>
              <Typography variant="h4" component="div">
                2.3TB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total audit data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<History />} label="Audit Events" />
          <Tab icon={<Message />} label="Message Logs" />
          <Tab icon={<Shield />} label="Security Alerts" />
          <Tab icon={<DataUsage />} label="Data Changes" />
          <Tab icon={<Computer />} label="System Access" />
          <Tab icon={<Analytics />} label="Analytics" />
          <Tab icon={<Archive />} label="Retention & Archive" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Audit Events ({filteredAuditEvents.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" startIcon={<FilterList />}>
                  Advanced Filter
                </Button>
                <Button variant="outlined" startIcon={<Download />}>
                  Export
                </Button>
              </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Severity</InputLabel>
                <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                  <MenuItem value="all">All Severity</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="authentication">Authentication</MenuItem>
                  <MenuItem value="user-management">User Management</MenuItem>
                  <MenuItem value="data-modification">Data Modification</MenuItem>
                  <MenuItem value="system-access">System Access</MenuItem>
                  <MenuItem value="security">Security</MenuItem>
                  <MenuItem value="financial">Financial</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Filter by user..."
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                sx={{ minWidth: 150 }}
              />
            </Box>

            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Typography variant="body1" color="text.secondary">
                  Loading audit logs...
                </Typography>
              </Box>
            )}

            {/* Error State */}
            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Error Loading Audit Logs</AlertTitle>
                {error}
              </Alert>
            )}

            {/* Empty State */}
            {!loading && !error && auditEvents.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Typography variant="body1" color="text.secondary">
                  No activity logs found
                </Typography>
              </Box>
            )}

            {/* Events Table */}
            {!loading && !error && auditEvents.length > 0 && (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Timestamp</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Resource</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAuditEvents.map((event) => (
                    <React.Fragment key={event.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleEventClick(event)}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedEvent(expandedEvent === event.id ? null : event.id);
                            }}
                          >
                            {expandedEvent === event.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(event.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {event.userName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{event.userName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {event.userRole}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{event.action}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getCategoryIcon(event.category)}
                            <Chip
                              label={event.category}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{event.resource}</Typography>
                          {event.resourceId && (
                            <Typography variant="caption" color="text.secondary">
                              ID: {event.resourceId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.severity}
                            size="small"
                            sx={{
                              backgroundColor: getSeverityColor(event.severity) + '20',
                              color: getSeverityColor(event.severity),
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.outcome}
                            size="small"
                            sx={{
                              backgroundColor: getOutcomeColor(event.outcome) + '20',
                              color: getOutcomeColor(event.outcome),
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{event.location}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {event.ipAddress}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getDeviceIcon(event.deviceType)}
                            <IconButton size="small">
                              <MoreVert />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0 }}>
                          <Collapse in={expandedEvent === event.id} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, pl: 4, backgroundColor: 'action.hover' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" color="text.secondary" gutterBottom>
                                    User Agent
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    {event.userAgent}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Session ID
                                  </Typography>
                                  <Typography variant="body2">
                                    {event.sessionId}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Details
                                  </Typography>
                                  <Box sx={{ backgroundColor: 'background.paper', p: 1, borderRadius: 1 }}>
                                    <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                                      {JSON.stringify(event.details, null, 2)}
                                    </pre>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Message Logs ({messageLogs.length})
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Conversation</TableCell>
                    <TableCell>Sender</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messageLogs.map((message) => (
                    <TableRow key={message.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(message.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {message.conversationId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{message.senderName}</Typography>
                          <Chip 
                            label={message.senderType} 
                            size="small" 
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{message.recipientName}</Typography>
                          <Chip 
                            label={message.recipientType} 
                            size="small" 
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap>
                            {message.content}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={message.messageType}
                              size="small"
                              variant="outlined"
                            />
                            {message.metadata.automated && (
                              <Chip label="Auto" size="small" color="info" />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getChannelIcon(message.channel)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {message.channel}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(message.status) + '20',
                            color: getStatusColor(message.status),
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.category}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {securityAlerts.map((alert) => (
            <Grid item xs={12} md={6} key={alert.id}>
              <Card sx={{
                borderLeft: `4px solid ${getSeverityColor(alert.severity)}`
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {alert.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(alert.timestamp)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={alert.severity}
                        size="small"
                        sx={{
                          backgroundColor: getSeverityColor(alert.severity) + '20',
                          color: getSeverityColor(alert.severity)
                        }}
                      />
                      <Chip
                        label={alert.status}
                        size="small"
                        color={alert.status === 'resolved' ? 'success' : alert.status === 'investigating' ? 'warning' : 'error'}
                      />
                    </Box>
                  </Box>

                  {alert.userName && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Affected User: {alert.userName} ({alert.userId})
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Indicators
                    </Typography>
                    <List dense>
                      {alert.indicators.map((indicator, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={indicator} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Location: {alert.location} • IP: {alert.ipAddress}
                    </Typography>
                    {alert.assignedTo && (
                      <Typography variant="body2" color="text.secondary">
                        Assigned to: {alert.assignedTo}
                      </Typography>
                    )}
                  </Box>

                  {alert.resolution && (
                    <Box sx={{ mb: 2, p: 1, backgroundColor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Resolution:</strong> {alert.resolution}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Resolved: {alert.resolvedAt && formatDateTime(alert.resolvedAt)}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined">
                      Investigate
                    </Button>
                    <Button size="small" variant="outlined">
                      Assign
                    </Button>
                    {alert.status !== 'resolved' && (
                      <Button size="small" variant="contained">
                        Resolve
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Data Changes ({dataChanges.length})
            </Typography>
            
            {dataChanges.map((change) => (
              <Accordion key={change.id}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                    <Box>
                      <Typography variant="subtitle1">
                        {change.operation} on {change.tableName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(change.timestamp)} by {change.userName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={change.operation}
                        size="small"
                        color={change.operation === 'INSERT' ? 'success' : change.operation === 'UPDATE' ? 'warning' : 'error'}
                      />
                      {change.rollbackAvailable && (
                        <Chip label="Rollback Available" size="small" color="info" />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Record Details
                      </Typography>
                      <Typography variant="body2">
                        Table: {change.tableName}<br />
                        Record ID: {change.recordId}<br />
                        Reason: {change.reason || 'No reason provided'}
                      </Typography>
                      {change.approvedBy && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Approved by: {change.approvedBy}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Field Changes
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Field</TableCell>
                              <TableCell>Old Value</TableCell>
                              <TableCell>New Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {change.fieldChanges.map((fieldChange, index) => (
                              <TableRow key={index}>
                                <TableCell>{fieldChange.field}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {fieldChange.oldValue?.toString() || 'null'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {fieldChange.newValue?.toString() || 'null'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                  {change.rollbackAvailable && (
                    <Box sx={{ mt: 2 }}>
                      <Button size="small" variant="outlined" color="warning">
                        Rollback Changes
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {selectedTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Access Logs ({systemAccessLogs.length})
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>MFA</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemAccessLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{log.userName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.userRole}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {log.action === 'login' ? <Login /> : 
                           log.action === 'logout' ? <Logout /> : 
                           log.action === 'access-denied' ? <Error /> : <VpnKey />}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {log.action.replace('-', ' ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.resource}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{log.location}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.ipAddress}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.sessionDuration ? formatDuration(log.sessionDuration) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.mfaUsed ? 'Yes' : 'No'}
                          size="small"
                          color={log.mfaUsed ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.outcome}
                          size="small"
                          sx={{
                            backgroundColor: getOutcomeColor(log.outcome) + '20',
                            color: getOutcomeColor(log.outcome),
                            textTransform: 'capitalize'
                          }}
                        />
                        {log.failureReason && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {log.failureReason}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 5 && (
        <Grid container spacing={3}>
          {/* Activity Over Time */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Activity Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={activityStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="events" stroke="#2196F3" strokeWidth={2} name="Audit Events" />
                    <Line type="monotone" dataKey="messages" stroke="#4CAF50" strokeWidth={2} name="Messages" />
                    <Line type="monotone" dataKey="alerts" stroke="#F44336" strokeWidth={2} name="Security Alerts" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Category Distribution */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Events by Category
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={categoryDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 6 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Data Retention Policies
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <History />
                    </ListItemIcon>
                    <ListItemText
                      primary="Audit Events"
                      secondary="Retained for 7 years, archived after 1 year"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Configure
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Message />
                    </ListItemIcon>
                    <ListItemText
                      primary="Message Logs"
                      secondary="Retained for 3 years, archived after 6 months"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Configure
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Shield />
                    </ListItemIcon>
                    <ListItemText
                      primary="Security Events"
                      secondary="Retained for 10 years, never archived"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Configure
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Archive Management
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Backup />
                    </ListItemIcon>
                    <ListItemText
                      primary="Last Backup"
                      secondary="2024-01-15 02:00 (2.3GB)"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Run Now
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Archive />
                    </ListItemIcon>
                    <ListItemText
                      primary="Archive Storage"
                      secondary="15.7GB archived (89% compression)"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Manage
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <RestoreFromTrash />
                    </ListItemIcon>
                    <ListItemText
                      primary="Data Recovery"
                      secondary="Point-in-time recovery available"
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" variant="outlined">
                        Restore
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Event Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Event Details</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Event Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Action:</strong> {selectedEvent.action}<br />
                    <strong>User:</strong> {selectedEvent.userName} ({selectedEvent.userRole})<br />
                    <strong>Timestamp:</strong> {formatDateTime(selectedEvent.timestamp)}<br />
                    <strong>Resource:</strong> {selectedEvent.resource}<br />
                    <strong>Outcome:</strong> {selectedEvent.outcome}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Technical Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>IP Address:</strong> {selectedEvent.ipAddress}<br />
                    <strong>Location:</strong> {selectedEvent.location}<br />
                    <strong>Device:</strong> {selectedEvent.deviceType}<br />
                    <strong>Session:</strong> {selectedEvent.sessionId}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Event Details
                  </Typography>
                  <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
                    <pre style={{ fontSize: '0.875rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedEvent.details, null, 2)}
                    </pre>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button variant="outlined">Export Event</Button>
        </DialogActions>
      </Dialog>

      {/* Retention Policy Dialog */}
      <Dialog open={retentionDialogOpen} onClose={() => setRetentionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Data Retention Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Configure how long different types of audit data are retained.
          </Typography>
          {/* Retention configuration form would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetentionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Configuration</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrailPage;